// DTOs/schemas da API pública `/v1` e das chaves de API. Este arquivo pertence ao pacote api-cliente.
//
// Os schemas zod daqui são a FONTE ÚNICA do contrato: a API valida com eles (ZodPipe) e o
// OpenAPI 3.1 de `/v1` (apps/site/public/openapi.json) é gerado a partir deles. Respostas
// também têm schema (z.infer tipa o retorno dos controllers e alimenta a documentação).
import { z } from "zod";

import {
  CATEGORIAS_META,
  EVENTOS_SAIDA,
  ORIGENS_MENSAGEM,
  STATUS_CAMPANHA,
  STATUS_LEAD,
  STATUS_MENSAGEM,
  STATUS_NUMERO,
  TIPOS_ENVIO,
  TIPOS_NUMERO,
} from "./enums";
import { LISTA_MAX_COLUNAS, LISTA_MAX_LINHAS, parseAgendamentoSaoPaulo, zMapeamento } from "./campanhas";
import { zErroEnvio, zIndicadoresNumero } from "./envio";
import { MAX_TEXTO_MENSAGEM, TIPOS_CONTEUDO } from "./inbox";
import { ORIGENS_LEAD } from "./leads";
import { RECURSOS } from "./recursos";
import { zTelefoneE164 } from "./numeros";

// ─── Constantes do produto ───────────────────────────────────────────────────

/** Prefixo de toda chave de API (`zh_live_<aleatório>`). */
export const PREFIXO_CHAVE_API = "zh_live_";
/** Chaves ATIVAS por conta (as revogadas não contam). */
export const MAX_CHAVES_API_ATIVAS = 10;
/** Limite de taxa por chave (janela deslizante de 1 minuto). */
export const LIMITE_REQUISICOES_POR_MINUTO = 600;
/** Quanto tempo a resposta de um `Idempotency-Key` fica guardada. */
export const IDEMPOTENCIA_TTL_HORAS = 24;

// ─── Chaves de API (painel; JWT, só OWNER) ───────────────────────────────────

/** `POST /chaves-api`. `numeroIds` (publicIds) vazio = todos os números da conta. */
export const zCriarChaveApi = z.object({
  nome: z
    .string({ required_error: "Informe um nome para a chave." })
    .trim()
    .min(1, "Informe um nome para a chave.")
    .max(80, "O nome pode ter no máximo 80 caracteres."),
  numeroIds: z
    .array(z.string().trim().min(1).max(64))
    .max(100)
    .default([])
    .transform((lista) => [...new Set(lista)]),
});
export type CriarChaveApi = z.output<typeof zCriarChaveApi>;

export interface ChaveApiDto {
  /** publicId */
  id: string;
  nome: string;
  /** Parte visível, ex.: `zh_live_ab12`. */
  prefixo: string;
  /** publicIds dos números permitidos; vazio = todos. */
  numeroIds: string[];
  criadoEm: string;
  ultimoUsoEm: string | null;
  /** Preenchido quando revogada (a chave deixa de autenticar na hora). */
  revogadaEm: string | null;
}

/** Resposta da criação: o valor COMPLETO só aparece aqui, uma única vez. */
export interface ChaveApiCriadaDto extends ChaveApiDto {
  chave: string;
}

// ─── Convenções comuns ───────────────────────────────────────────────────────

const idPublico = z.string({ invalid_type_error: "Informe um identificador válido." }).trim().min(1, "Informe o identificador.").max(64);
const idObrigatorio = (rotulo: string) =>
  z.string({ required_error: `Informe ${rotulo}.`, invalid_type_error: `Informe ${rotulo}.` }).trim().min(1, `Informe ${rotulo}.`).max(64);

const numeroIdCampo = idObrigatorio("o número (numeroId)").describe("Identificador público do número (`GET /v1/numeros`).");
const paraCampo = z
  .string({ required_error: "Informe o telefone de destino." })
  .trim()
  .min(8, "Telefone inválido.")
  .max(30, "Telefone inválido.")
  .describe(
    "Telefone do contato com DDI e DDD. Aceita máscara (`+55 (11) 98765-4321`); sem DDI e com 10 ou 11 dígitos assume o Brasil (55). Normalizado para E.164 sem `+`.",
  );

/** Erro padrão de toda resposta não-2xx. */
export const zApiError = z.object({
  statusCode: z.number().int(),
  code: z.string().describe("Código estável (ex.: `nao_encontrado`, `campanha_exige_api_oficial`). Ramifique por ele."),
  message: z.string().describe("Texto em português para humanos. Não ramifique por ele."),
  fields: z.record(z.array(z.string())).optional().describe("Erros por campo, quando a validação falha."),
});
export type ApiErrorDto = z.infer<typeof zApiError>;

// ─── Números ─────────────────────────────────────────────────────────────────

export const zNumeroApi = z.object({
  id: z.string(),
  tipo: z.enum(TIPOS_NUMERO),
  apelido: z.string(),
  status: z.enum(STATUS_NUMERO),
  telefone: z.string().nullable().describe("E.164 sem `+`. Nulo até conectar (não oficial) ou validar (oficial)."),
  nomePerfil: z.string().nullable(),
  ritmoPorMinuto: z.number().int().nullable().describe("Só NAO_OFICIAL: cadência de saída (mensagens/minuto)."),
  criadoEm: z.string(),
  ultimaAtividadeEm: z.string().nullable(),
  oficial: z
    .object({
      qualityRating: z.string().nullable(),
      nomeVerificado: z.string().nullable(),
      alertaPagamento: z.boolean(),
      statusMotivo: z.string().nullable(),
      vazaoPorSegundo: z.number().int(),
    })
    .nullable(),
  naoOficial: z.object({ conectadoEm: z.string().nullable(), protegidoPorProxy: z.boolean() }).nullable(),
  indicadores: zIndicadoresNumero.optional().describe("Só em `GET /v1/numeros/{id}`."),
  recursos: z.array(z.enum(RECURSOS)).describe("O que o número suporta, conforme o tipo. Rota fora da lista responde 422 `recurso_indisponivel`."),
});
export type NumeroApi = z.infer<typeof zNumeroApi>;

export const zListaNumerosApi = z.object({ numeros: z.array(zNumeroApi) });
export type ListaNumerosApi = z.infer<typeof zListaNumerosApi>;

/** `POST /v1/numeros/{id}/conectar`. Sem `telefone` = QR Code; com `telefone` = código de pareamento. */
export const zConectarNumeroApi = z.object({
  telefone: zTelefoneE164.optional().describe("Com telefone, devolve o código de pareamento de 8 caracteres em vez do QR."),
});
export type ConectarNumeroApi = z.output<typeof zConectarNumeroApi>;

export const zConexaoNumeroApi = z.object({
  numero: zNumeroApi,
  modo: z.enum(["QR", "CODIGO"]),
  qrcode: z.string().nullable().describe("Imagem do QR (data URL PNG). Pode ser nula se o primeiro QR ainda não foi gerado: ele chega pelo evento `numero.qrcode`."),
  codigo: z.string().nullable().describe("Texto do QR, para renderizar no seu sistema."),
  codigoPareamento: z.string().nullable(),
  expiraEm: z.string().describe("Depois disso o QR deixa de ser renovado; chame `conectar` de novo."),
});
export type ConexaoNumeroApi = z.infer<typeof zConexaoNumeroApi>;

export const zTemplateApi = z.object({
  id: z.string().describe("Use em `templateId` (mensagens e campanhas)."),
  nome: z.string(),
  idioma: z.string(),
  categoria: z.enum(CATEGORIAS_META),
  status: z.string().describe("Status na Meta: APPROVED, PENDING, REJECTED, PAUSED, DISABLED ou INDISPONIVEL."),
  motivo: z.string().nullable(),
  totalVariaveis: z.number().int(),
  variaveis: z.array(z.record(z.unknown())),
  sincronizadoEm: z.string(),
});
export type TemplateApi = z.infer<typeof zTemplateApi>;
export const zListaTemplatesApi = z.object({ templates: z.array(zTemplateApi) });
export type ListaTemplatesApi = z.infer<typeof zListaTemplatesApi>;

// ─── Mensagens ───────────────────────────────────────────────────────────────

/** Opções comuns dos envios de conteúdo (F3). */
const opcoesEnvio = {
  respondendoA: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .optional()
    .describe("`id` de uma mensagem da mesma conversa: o envio sai citando ela."),
  mencoes: z
    .array(z.string().trim().regex(/^\d{8,15}$/, "Telefone inválido."))
    .max(50, "Máximo de 50 menções.")
    .optional()
    .describe("Telefones mencionados (só em grupos; em conversa individual é aceito e ignorado). Só NAO_OFICIAL."),
  grupoId: z.string().trim().min(1).max(64).optional().describe("Envia ao grupo (`id` de `GET /v1/numeros/{id}/grupos`) em vez de `para`. Só NAO_OFICIAL."),
  mencionarTodos: z.boolean().optional().describe("Só em grupo: menciona todos os participantes."),
};

/** Destino do envio: um contato (`para`) ou um grupo (`grupoId`), nunca os dois. */
const paraOuGrupo = (d: { para?: string; grupoId?: string; mencionarTodos?: boolean }, ctx: z.RefinementCtx) => {
  if (!d.para === !d.grupoId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["para"], message: "Informe `para` ou `grupoId` (um dos dois)." });
  if (d.mencionarTodos && !d.grupoId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["mencionarTodos"], message: "`mencionarTodos` só vale para grupo." });
};

export const zEnviarTextoApi = z.object({
  numeroId: numeroIdCampo,
  para: paraCampo.optional(),
  texto: z
    .string({ required_error: "Escreva a mensagem." })
    .trim()
    .min(1, "Escreva a mensagem.")
    .max(MAX_TEXTO_MENSAGEM, `Máximo de ${MAX_TEXTO_MENSAGEM} caracteres.`),
  previaLink: z.boolean().optional().describe("Pede a prévia do primeiro link do texto."),
  ...opcoesEnvio,
}).superRefine(paraOuGrupo);
export type EnviarTextoApi = z.output<typeof zEnviarTextoApi>;

export const TIPOS_MIDIA_API = ["imagem", "documento", "audio", "video"] as const;
export type TipoMidiaApi = (typeof TIPOS_MIDIA_API)[number];

/** Arquivo em base64 (alternativa à URL): até 16 MB decodificados, o tipo é conferido pelos bytes. */
const arquivoBase64 = {
  base64: z.string().max(23_000_000, "O arquivo passa de 16 MB.").optional().describe("Conteúdo em base64 (ou data URL). Alternativa a `url`."),
  mimeType: z
    .string()
    .trim()
    .regex(/^[a-z]+\/[a-z0-9.+-]+$/i, "MIME inválido.")
    .optional()
    .describe("Obrigatório com `base64` (ex.: application/pdf)."),
};
const urlOuBase64 = (d: { url?: string; base64?: string; mimeType?: string }, ctx: z.RefinementCtx) => {
  if (!d.url === !d.base64) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["url"], message: "Informe `url` ou `base64` (um dos dois)." });
  if (d.base64 && !d.mimeType) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["mimeType"], message: "Informe o mimeType do arquivo." });
};

export const zEnviarMidiaApi = z
  .object({
    numeroId: numeroIdCampo,
    para: paraCampo.optional(),
    tipo: z.enum(TIPOS_MIDIA_API),
    url: z
      .string()
      .trim()
      .min(1, "Informe a URL da mídia.")
      .max(2048, "URL longa demais.")
      .optional()
      .describe("URL HTTPS pública. http, hosts internos e IPs privados/loopback são recusados (422)."),
    ...arquivoBase64,
    legenda: z.string().trim().max(1024).optional(),
    nomeArquivo: z.string().trim().min(1).max(255).optional().describe("Nome exibido para documentos."),
    ...opcoesEnvio,
  })
  .superRefine(urlOuBase64)
  .superRefine(paraOuGrupo);
export type EnviarMidiaApi = z.output<typeof zEnviarMidiaApi>;

export const zEnviarTemplateApi = z.object({
  numeroId: numeroIdCampo.describe("Número OFICIAL."),
  para: paraCampo,
  templateId: idObrigatorio("o template (templateId)").describe("`id` do template em `GET /v1/numeros/{id}/templates`."),
  variaveis: z
    .array(z.string().max(1024))
    .max(30)
    .default([])
    .describe("Valores das variáveis `{{1}}`, `{{2}}`… na ordem (cabeçalho, corpo, botões)."),
});
export type EnviarTemplateApi = z.output<typeof zEnviarTemplateApi>;

export const zEnviarLocalizacaoApi = z.object({
  numeroId: numeroIdCampo,
  para: paraCampo.optional(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  nome: z.string().trim().max(256).optional(),
  endereco: z.string().trim().max(512).optional(),
  ...opcoesEnvio,
}).superRefine(paraOuGrupo);
export type EnviarLocalizacaoApi = z.output<typeof zEnviarLocalizacaoApi>;

export const zContatoCompartilhado = z.object({
  nome: z.string().trim().min(1, "Informe o nome do contato.").max(256),
  telefones: z.array(z.string().trim().regex(/^\+?\d{8,15}$/, "Telefone inválido.")).min(1).max(5),
  empresa: z.string().trim().max(256).optional(),
  email: z.string().trim().email("E-mail inválido.").max(256).optional(),
});
export type ContatoCompartilhado = z.output<typeof zContatoCompartilhado>;

export const zEnviarContatoApi = z.object({
  numeroId: numeroIdCampo,
  para: paraCampo.optional(),
  contatos: z.array(zContatoCompartilhado).min(1).max(10, "Máximo de 10 contatos."),
  ...opcoesEnvio,
}).superRefine(paraOuGrupo);
export type EnviarContatoApi = z.output<typeof zEnviarContatoApi>;

export const zEnviarFigurinhaApi = z
  .object({
    numeroId: numeroIdCampo,
    para: paraCampo.optional(),
    url: z.string().trim().min(1, "Informe a URL da figurinha.").max(2048).optional().describe("URL HTTPS pública de um WebP (até 500 KB; animada até 1 MB)."),
    ...arquivoBase64,
    ...opcoesEnvio,
  })
  .superRefine(urlOuBase64)
  .superRefine(paraOuGrupo);
export type EnviarFigurinhaApi = z.output<typeof zEnviarFigurinhaApi>;

export const zEnviarEnqueteApi = z
  .object({
    numeroId: numeroIdCampo.describe("Número NAO_OFICIAL."),
    para: paraCampo.optional(),
    pergunta: z.string().trim().min(1, "Escreva a pergunta.").max(255),
    opcoes: z
      .array(z.string().trim().min(1).max(100))
      .min(2, "Mínimo de 2 opções.")
      .max(12, "Máximo de 12 opções.")
      .refine((o) => new Set(o).size === o.length, "As opções precisam ser diferentes."),
    maxRespostas: z.number().int().min(1).default(1).describe("Quantas opções cada contato pode marcar."),
    ...opcoesEnvio,
  })
  .refine((d) => d.maxRespostas <= d.opcoes.length, { message: "maxRespostas passa do número de opções.", path: ["maxRespostas"] })
  .superRefine(paraOuGrupo);
export type EnviarEnqueteApi = z.output<typeof zEnviarEnqueteApi>;

// ─── Interativos (F6, só oficial) ────────────────────────────────────────────

export const TIPOS_INTERATIVO = ["botoes", "lista", "link"] as const;

export const zEnviarInterativaApi = z
  .object({
    numeroId: numeroIdCampo.describe("Número OFICIAL."),
    para: paraCampo,
    tipo: z.enum(TIPOS_INTERATIVO),
    corpo: z.string().trim().min(1, "Escreva o texto da mensagem.").max(1024, "Máximo de 1.024 caracteres."),
    cabecalho: z.string().trim().max(60, "Máximo de 60 caracteres.").optional(),
    rodape: z.string().trim().max(60, "Máximo de 60 caracteres.").optional(),
    botoes: z
      .array(z.object({ id: z.string().trim().min(1).max(256), titulo: z.string().trim().min(1).max(20, "O título do botão tem até 20 caracteres.") }))
      .min(1)
      .max(3, "Máximo de 3 botões.")
      .optional()
      .describe("Só em `botoes`."),
    textoBotao: z.string().trim().min(1).max(20, "Até 20 caracteres.").optional().describe("Em `lista` e `link`: o texto do botão."),
    secoes: z
      .array(
        z.object({
          titulo: z.string().trim().max(24).optional(),
          itens: z
            .array(z.object({ id: z.string().trim().min(1).max(200), titulo: z.string().trim().min(1).max(24, "O item tem até 24 caracteres."), descricao: z.string().trim().max(72).optional() }))
            .min(1),
        }),
      )
      .min(1)
      .max(10)
      .optional()
      .describe("Só em `lista`: até 10 itens no total."),
    url: z.string().trim().url().max(2048).optional().describe("Só em `link`: URL HTTPS do botão."),
    respondendoA: z.string().trim().min(1).max(64).optional(),
  })
  .superRefine((d, ctx) => {
    const erro = (path: string, message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if (d.tipo === "botoes" && !d.botoes?.length) erro("botoes", "Informe de 1 a 3 botões.");
    if (d.tipo === "botoes" && d.botoes && new Set(d.botoes.map((b) => b.id)).size !== d.botoes.length) erro("botoes", "Os ids dos botões precisam ser diferentes.");
    if (d.tipo === "lista") {
      if (!d.textoBotao) erro("textoBotao", "Informe o texto do botão da lista.");
      const total = (d.secoes ?? []).reduce((t, s) => t + s.itens.length, 0);
      if (!total) erro("secoes", "Informe ao menos uma seção com itens.");
      if (total > 10) erro("secoes", "A lista aceita até 10 itens no total.");
    }
    if (d.tipo === "link") {
      if (!d.textoBotao) erro("textoBotao", "Informe o texto do botão.");
      if (!d.url?.startsWith("https://")) erro("url", "Informe uma URL HTTPS.");
    }
  });
export type EnviarInterativaApi = z.output<typeof zEnviarInterativaApi>;

// ─── Status / stories (F6, só não oficial) ──────────────────────────────────

export const zPublicarStatusApi = z
  .object({
    tipo: z.enum(["texto", "midia"]),
    texto: z.string().trim().min(1).max(700, "Máximo de 700 caracteres.").optional().describe("Só em `texto`. O Evolution Go publica o texto sem cor de fundo nem fonte."),
    midia: z
      .object({
        tipo: z.enum(["imagem", "video"]),
        url: z.string().trim().max(2048).optional().describe("URL HTTPS pública."),
        base64: z.string().max(23_000_000).optional(),
        mimeType: z.string().trim().max(100).optional(),
      })
      .optional()
      .describe("Só em `midia`: `url` ou `base64` (com `mimeType`)."),
    legenda: z.string().trim().max(700).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.tipo === "texto" && !d.texto) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["texto"], message: "Escreva o texto do status." });
    if (d.tipo === "midia") {
      if (!d.midia) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["midia"], message: "Informe a mídia." });
      else if (!d.midia.url === !d.midia.base64) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["midia", "url"], message: "Informe `url` ou `base64`." });
      else if (d.midia.base64 && !d.midia.mimeType) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["midia", "mimeType"], message: "Informe o mimeType." });
    }
  });
export type PublicarStatusApi = z.output<typeof zPublicarStatusApi>;
export const zStatusPublicadoApi = z.object({ restanteHoje: z.number().int() });
export type StatusPublicadoApi = z.infer<typeof zStatusPublicadoApi>;

// ─── Etiquetas (F6, só não oficial com WhatsApp Business) ───────────────────

export const zEtiquetaApi = z.object({ id: z.string(), nome: z.string(), cor: z.number().int().describe("Índice da cor do WhatsApp (0 a 19).") });
export type EtiquetaApi = z.infer<typeof zEtiquetaApi>;
export const zListaEtiquetasApi = z.object({ etiquetas: z.array(zEtiquetaApi) });
export type ListaEtiquetasApi = z.infer<typeof zListaEtiquetasApi>;
export const zSalvarEtiquetaApi = z.object({
  etiquetaId: z.string().trim().min(1).max(64).optional().describe("Para editar uma existente; sem ele, cria."),
  nome: z.string().trim().min(1, "Informe o nome.").max(100),
  cor: z.number().int().min(0).max(19).default(0),
});
export type SalvarEtiquetaApi = z.output<typeof zSalvarEtiquetaApi>;
export const zAplicarEtiquetaApi = z.object({ etiquetaId: z.string().trim().min(1).max(64), acao: z.enum(["aplicar", "remover"]) });
export type AplicarEtiquetaApi = z.output<typeof zAplicarEtiquetaApi>;

// ─── Comunidades e canais (F6, só não oficial) ──────────────────────────────

export const zCriarComunidadeApi = z.object({ numeroId: numeroIdCampo, nome: z.string().trim().min(1, "Informe o nome.").max(100) });
export type CriarComunidadeApi = z.output<typeof zCriarComunidadeApi>;
export const zComunidadeApi = z.object({ id: z.string().describe("JID da comunidade no WhatsApp (`...@g.us`)."), nome: z.string() });
export type ComunidadeApi = z.infer<typeof zComunidadeApi>;
export const zGruposComunidadeApi = z.object({
  numeroId: numeroIdCampo,
  acao: z.enum(["adicionar", "remover"]),
  grupoIds: z.array(z.string().trim().min(1).max(64)).min(1).max(50).describe("`id` dos grupos (de `GET /v1/numeros/{id}/grupos`)."),
});
export type GruposComunidadeApi = z.output<typeof zGruposComunidadeApi>;

export const zCanalApi = z.object({
  id: z.string().describe("JID do canal (`...@newsletter`)."),
  nome: z.string().nullable(),
  descricao: z.string().nullable(),
  seguidores: z.number().int().nullable(),
  convite: z.string().nullable().describe("Link de convite do canal."),
  papel: z.string().nullable().describe("Papel do número no canal (`owner`, `admin`, `subscriber`…)."),
});
export type CanalApi = z.infer<typeof zCanalApi>;
export const zListaCanaisApi = z.object({ canais: z.array(zCanalApi) });
export type ListaCanaisApi = z.infer<typeof zListaCanaisApi>;
export const zCriarCanalApi = z.object({ nome: z.string().trim().min(1, "Informe o nome.").max(100), descricao: z.string().trim().max(2048).optional() });
export type CriarCanalApi = z.output<typeof zCriarCanalApi>;
export const zConviteCanalApi = z.object({
  numeroId: numeroIdCampo,
  link: z.string().trim().regex(/^(https:\/\/whatsapp\.com\/channel\/)?[A-Za-z0-9]{10,40}$/, "Link de canal inválido."),
});
export type ConviteCanalApi = z.output<typeof zConviteCanalApi>;
export const zConsultaMensagensCanalApi = z.object({
  numeroId: numeroIdCampo,
  quantidade: z.coerce.number().int().min(1).max(100).default(20),
  antesDe: z.coerce.number().int().min(1).optional().describe("`servidorId` da mensagem mais antiga da página anterior."),
});
export type ConsultaMensagensCanalApi = z.output<typeof zConsultaMensagensCanalApi>;
export const zMensagensCanalApi = z.object({
  mensagens: z.array(
    z.object({
      id: z.string(),
      servidorId: z.number().int(),
      tipo: z.string(),
      texto: z.string().nullable(),
      enviadaEm: z.string().nullable(),
      visualizacoes: z.number().int(),
      reacoes: z.record(z.number().int()),
    }),
  ),
  antesDe: z.number().int().nullable().describe("Passe em `antesDe` para a página seguinte (mais antiga)."),
});
export type MensagensCanalApi = z.infer<typeof zMensagensCanalApi>;

// ─── Operações em mensagens (F3) ─────────────────────────────────────────────

/** Prazo do WhatsApp para editar uma mensagem enviada. */
export const PRAZO_EDICAO_MS = 15 * 60_000;
/** Prazo do WhatsApp para apagar para todos. */
export const PRAZO_EXCLUSAO_MS = 2 * 24 * 3_600_000;
export const MAX_DURACAO_PRESENCA_MS = 25_000;

export const zReagirMensagemApi = z.object({
  emoji: z.string().max(16, "Use um único emoji.").describe("Vazio remove a reação do número."),
});
export type ReagirMensagemApi = z.output<typeof zReagirMensagemApi>;

export const zEditarMensagemApi = z.object({
  texto: z
    .string({ required_error: "Escreva o texto novo." })
    .trim()
    .min(1, "Escreva o texto novo.")
    .max(MAX_TEXTO_MENSAGEM, `Máximo de ${MAX_TEXTO_MENSAGEM} caracteres.`),
});
export type EditarMensagemApi = z.output<typeof zEditarMensagemApi>;

export const ESTADOS_PRESENCA = ["digitando", "gravando", "parou"] as const;

export const zPresencaApi = z.object({
  numeroId: numeroIdCampo,
  para: paraCampo,
  estado: z.enum(ESTADOS_PRESENCA),
  duracaoMs: z
    .number()
    .int()
    .min(1_000)
    .max(MAX_DURACAO_PRESENCA_MS, `Máximo de ${MAX_DURACAO_PRESENCA_MS} ms.`)
    .default(5_000)
    .describe("Quanto tempo o indicador fica visível (não oficial). No oficial, a Meta mostra por até 25 s ou até a resposta."),
});
export type PresencaApi = z.output<typeof zPresencaApi>;

export const zVotosEnqueteApi = z.object({
  mensagemId: z.string(),
  pergunta: z.string().nullable(),
  maxRespostas: z.number().int(),
  opcoes: z
    .array(z.object({ opcao: z.string(), total: z.number().int(), telefones: z.array(z.string()) }))
    .describe("Na ordem da enquete, com o total de votos atuais."),
  votos: z
    .array(z.object({ telefone: z.string(), opcoes: z.array(z.string()), votadoEm: z.string() }))
    .describe("Voto atual de cada contato (trocar o voto substitui o anterior)."),
});
export type VotosEnqueteApi = z.infer<typeof zVotosEnqueteApi>;

// ─── Grupos (F5) ─────────────────────────────────────────────────────────────

const telefonesGrupo = z.array(z.string().trim().min(8, "Telefone inválido.").max(30, "Telefone inválido.")).min(1).max(50, "Máximo de 50 por chamada.");

export const zParticipanteGrupoApi = z.object({
  telefone: z.string().nullable().describe("Nulo quando o WhatsApp só informa o identificador privado."),
  admin: z.boolean(),
  superAdmin: z.boolean(),
});

export const zGrupoApi = z.object({
  id: z.string(),
  numeroId: z.string(),
  nome: z.string().nullable(),
  descricao: z.string().nullable(),
  soAdminsEnviam: z.boolean(),
  soAdminsEditam: z.boolean(),
  numeroEhAdmin: z.boolean(),
  participantes: z.array(zParticipanteGrupoApi).optional().describe("Só em `GET /v1/grupos/{id}` (lido do WhatsApp, cache de 1 minuto)."),
  totalParticipantes: z.number().int().optional(),
});
export type GrupoApi = z.infer<typeof zGrupoApi>;
export const zListaGruposApi = z.object({ grupos: z.array(zGrupoApi) });
export type ListaGruposApi = z.infer<typeof zListaGruposApi>;

export const zCriarGrupoApi = z.object({
  numeroId: numeroIdCampo.describe("Número NAO_OFICIAL conectado."),
  nome: z.string().trim().min(1, "Informe o nome.").max(100, "O WhatsApp aceita até 100 caracteres."),
  participantes: telefonesGrupo,
});
export type CriarGrupoApi = z.output<typeof zCriarGrupoApi>;

export const zEditarGrupoApi = z
  .object({
    nome: z.string().trim().min(1).max(100).optional(),
    descricao: z.string().trim().max(2048).optional(),
    foto: z.string().max(7_000_000).optional().describe("JPEG ou PNG: URL HTTPS pública ou base64."),
    soAdminsEnviam: z.boolean().optional(),
    soAdminsEditam: z.boolean().optional(),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined), { message: "Informe ao menos um campo.", path: ["nome"] });
export type EditarGrupoApi = z.output<typeof zEditarGrupoApi>;

export const ACOES_PARTICIPANTES = ["adicionar", "remover", "promover", "rebaixar"] as const;
export const zParticipantesGrupoAcaoApi = z.object({ acao: z.enum(ACOES_PARTICIPANTES), telefones: telefonesGrupo });
export type ParticipantesGrupoAcaoApi = z.output<typeof zParticipantesGrupoAcaoApi>;

export const zConviteGrupoApi = z.object({ link: z.string() });
export type ConviteGrupoApi = z.infer<typeof zConviteGrupoApi>;

export const zEntrarGrupoApi = z.object({
  numeroId: numeroIdCampo,
  link: z.string().trim().regex(/^(https:\/\/chat\.whatsapp\.com\/)?[A-Za-z0-9]{10,40}$/, "Link de convite inválido."),
});
export type EntrarGrupoApi = z.output<typeof zEntrarGrupoApi>;

export const zCustoEstimadoTemplate = z.object({
  categoria: z.enum(CATEGORIAS_META),
  centavos: z.number().int().describe("Estimativa do que a Meta cobra por esta mensagem, em centavos de BRL."),
  precoUsd: z.string(),
  cambio: z.string(),
  tabelaVersao: z.number().int(),
  estimado: z.literal(true),
});
export type CustoEstimadoTemplate = z.infer<typeof zCustoEstimadoTemplate>;

/** Corpo do 202 dos envios individuais. Falha do canal NÃO é erro HTTP: vem em `status: "FALHOU"` + `erro`. */
export const zResultadoEnvioApi = z.object({
  id: z.string().describe("Identificador da mensagem (`GET /v1/mensagens/{id}`). O resultado final chega em `mensagem.status`."),
  tipoNumero: z.enum(TIPOS_NUMERO),
  tipoEnvio: z.enum(TIPOS_ENVIO),
  status: z.enum(STATUS_MENSAGEM),
  enfileirada: z.boolean().describe("true = na fila de cadência do número (não oficial); sai logo em seguida."),
  custoEvitado: z
    .object({ marketingCentavos: z.number().int(), utilityCentavos: z.number().int() })
    .nullable()
    .describe("Só números NAO_OFICIAL: quanto custaria a mesma mensagem na API Oficial."),
  contatoOptout: z.boolean().describe("O contato pediu para sair; o envio não é bloqueado, só sinalizado."),
  janelaAberta: z.boolean().describe("Janela de 24h aberta (informativo)."),
  erro: zErroEnvio.optional().describe("Só com `status: FALHOU`: o erro do canal (na Meta, com o código dela)."),
  custoEstimado: zCustoEstimadoTemplate.nullable().optional().describe("Só em templates: estimativa pela tabela pública."),
});
export type ResultadoEnvioApi = z.infer<typeof zResultadoEnvioApi>;

export const zMensagemApi = z.object({
  id: z.string(),
  numeroId: z.string(),
  tipoNumero: z.enum(TIPOS_NUMERO),
  conversaId: z.string(),
  contato: z.object({ telefone: z.string() }),
  direcao: z.enum(["ENTRADA", "SAIDA"]),
  origem: z.enum(ORIGENS_MENSAGEM),
  tipoEnvio: z.enum(TIPOS_ENVIO).nullable(),
  tipoConteudo: z.enum(TIPOS_CONTEUDO),
  texto: z.string().nullable().describe("Nulo depois da retenção (`removida`)."),
  midia: z
    .object({
      tipo: z.enum(["IMAGEM", "AUDIO", "VIDEO", "DOCUMENTO"]),
      mimeType: z.string().nullable(),
      disponivel: z.boolean(),
    })
    .nullable(),
  status: z.enum(STATUS_MENSAGEM),
  enfileirada: z.boolean(),
  contatoOptout: z.boolean(),
  custoEvitado: z.object({ marketingCentavos: z.number().int(), utilityCentavos: z.number().int() }).nullable(),
  erro: z.object({ codigo: z.string().nullable(), mensagem: z.string().nullable() }).nullable(),
  criadoEm: z.string(),
  enviadoEm: z.string().nullable(),
  entregueEm: z.string().nullable(),
  lidoEm: z.string().nullable(),
  removida: z.boolean().describe("Conteúdo removido pela retenção de 90 dias; os metadados permanecem."),
  editadaEm: z.string().nullable().describe("O autor editou a mensagem (o texto já é o novo)."),
  apagadaEm: z.string().nullable().describe("O autor apagou para todos: texto e mídia foram limpos."),
  reacoes: z.array(z.object({ autor: z.enum(["contato", "numero"]), emoji: z.string() })).describe("Reação atual de cada lado."),
  contatos: z
    .array(z.object({ nome: z.string(), telefones: z.array(z.string()), empresa: z.string().optional(), email: z.string().optional() }))
    .nullable()
    .describe("Só em `tipoConteudo: CONTATO`."),
  enquete: z
    .object({ maxRespostas: z.number().int(), opcoes: z.array(z.object({ opcao: z.string(), total: z.number().int() })) })
    .nullable()
    .describe("Só em enquetes enviadas pelo número; o detalhe por contato está em `GET /v1/mensagens/{id}/votos`."),
  respondendoA: z.object({ id: z.string(), trecho: z.string().nullable() }).nullable().describe("Mensagem citada."),
  remetente: z.object({ telefone: z.string().nullable(), nome: z.string().nullable() }).nullable().describe("Só em grupo: quem escreveu."),
  interativo: z
    .object({ tipo: z.enum(TIPOS_INTERATIVO), opcoes: z.array(z.string()) })
    .nullable()
    .describe("Mensagem interativa enviada (oficial): o tipo e os títulos das opções."),
  interacao: z
    .object({ tipo: z.enum(["botao", "lista"]), id: z.string(), titulo: z.string() })
    .nullable()
    .describe("Resposta do contato a uma interativa: o botão ou item escolhido."),
});
export type MensagemApi = z.infer<typeof zMensagemApi>;

// ─── Conversas ───────────────────────────────────────────────────────────────

export const zConsultaConversasApi = z.object({
  numeroId: numeroIdCampo,
  telefone: paraCampo,
  cursor: z.string().trim().min(1).max(200).optional().describe("`proximoCursor` da página anterior (mensagens mais antigas)."),
  limite: z.coerce.number().int().min(1).max(100).default(40),
});
export type ConsultaConversasApi = z.output<typeof zConsultaConversasApi>;

export const zConversaApi = z.object({
  id: z.string(),
  numeroId: z.string(),
  tipoNumero: z.enum(TIPOS_NUMERO),
  contato: z.object({ telefone: z.string(), nome: z.string().nullable() }),
  ultimaMensagemEm: z.string(),
  ultimaEntradaEm: z.string().nullable(),
  janela24h: z.object({ aberta: z.boolean(), expiraEm: z.string().nullable() }),
  tipo: z.enum(["CONTATO", "GRUPO"]).describe("Em GRUPO, `contato.telefone` é o JID do grupo e `contato.nome` o nome dele."),
  etiquetas: z.array(z.object({ id: z.string(), nome: z.string(), cor: z.number().int() })).describe("Etiquetas do WhatsApp Business aplicadas (não oficial)."),
  arquivada: z.boolean(),
  fixada: z.boolean(),
  silenciadaAte: z.string().nullable().describe("Fim do silêncio; muito no futuro = sempre."),
});
export type ConversaApi = z.infer<typeof zConversaApi>;

// ─── Contatos, perfil e configurações (F4) ──────────────────────────────────

const telefoneLote = z.string().trim().min(8, "Telefone inválido.").max(30, "Telefone inválido.");

export const zVerificarContatosApi = z.object({
  numeroId: numeroIdCampo.describe("Número NAO_OFICIAL conectado."),
  telefones: z.array(telefoneLote).min(1).max(100, "Máximo de 100 telefones por chamada."),
});
export type VerificarContatosApi = z.output<typeof zVerificarContatosApi>;

export const zVerificacaoContatosApi = z.object({
  resultados: z.array(
    z.object({
      telefone: z.string().describe("Como foi enviado."),
      temWhatsapp: z.boolean(),
      telefoneNormalizado: z.string().nullable().describe("Como o WhatsApp conhece o número (ex.: com ou sem o nono dígito)."),
      contaComercial: z.boolean(),
    }),
  ),
  restanteNaHora: z.number().int().describe("Quantos telefones este número ainda pode verificar na hora corrente."),
});
export type VerificacaoContatosApi = z.infer<typeof zVerificacaoContatosApi>;

export const zConsultaNumeroApi = z.object({ numeroId: numeroIdCampo });
export type ConsultaNumeroApi = z.output<typeof zConsultaNumeroApi>;

export const zContatoInfoApi = z.object({
  telefone: z.string(),
  temWhatsapp: z.boolean(),
  nome: z.string().nullable().describe("Nome comercial verificado, quando houver."),
  recado: z.string().nullable(),
  fotoUrl: z.string().nullable().describe("Link temporário do WhatsApp; nulo se a privacidade do contato esconde a foto."),
  fotoExpiraEm: z.string().nullable(),
  contaComercial: z.boolean(),
});
export type ContatoInfoApi = z.infer<typeof zContatoInfoApi>;

export const zBloqueadosApi = z.object({ telefones: z.array(z.string()) });
export type BloqueadosApi = z.infer<typeof zBloqueadosApi>;

export const zConsultaAgendaApi = z.object({
  cursor: z.coerce.number().int().min(0).default(0).describe("Posição (o `proximoCursor` da página anterior)."),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});
export type ConsultaAgendaApi = z.output<typeof zConsultaAgendaApi>;

export const zAgendaApi = z.object({
  itens: z.array(
    z.object({
      telefone: z.string(),
      nome: z.string().nullable().describe("Nome salvo na agenda do celular."),
      nomePublico: z.string().nullable(),
      nomeComercial: z.string().nullable(),
    }),
  ),
  total: z.number().int(),
  proximoCursor: z.number().int().nullable(),
});
export type AgendaApi = z.infer<typeof zAgendaApi>;

export const zPerfilNumeroApi = z
  .object({
    nome: z.string().trim().min(1).max(25, "O WhatsApp aceita até 25 caracteres.").optional(),
    recado: z.string().trim().max(139, "O WhatsApp aceita até 139 caracteres.").optional(),
    foto: z.string().max(7_000_000, "A foto passa de 5 MB.").optional().describe("JPEG ou PNG em base64 (ou data URL), até 5 MB."),
  })
  .refine((d) => d.nome !== undefined || d.recado !== undefined || d.foto !== undefined, { message: "Informe nome, recado ou foto.", path: ["nome"] });
export type PerfilNumeroApi = z.output<typeof zPerfilNumeroApi>;

export const VALORES_PRIVACIDADE = ["todos", "contatos", "contatos_exceto", "ninguem"] as const;
const valorPrivacidade = z.enum(VALORES_PRIVACIDADE);

export const zPrivacidadeApi = z.object({
  vistoPorUltimo: valorPrivacidade,
  foto: valorPrivacidade,
  recado: valorPrivacidade,
  adicionarGrupos: valorPrivacidade,
  confirmacaoLeitura: z.enum(["todos", "ninguem"]),
});
export type PrivacidadeApi = z.infer<typeof zPrivacidadeApi>;
export const zAtualizarPrivacidadeApi = zPrivacidadeApi.partial().refine((d) => Object.keys(d).length > 0, { message: "Informe ao menos uma opção." });
export type AtualizarPrivacidadeApi = z.output<typeof zAtualizarPrivacidadeApi>;

export const zConfiguracoesNumeroApi = z.object({
  rejeitarChamadas: z.boolean(),
  mensagemRejeicao: z.string().max(500).nullable(),
  sempreOnline: z.boolean().describe("Aumenta o risco de banimento: comportamento não humano."),
  lerAutomaticamente: z.boolean().describe("Aumenta o risco de banimento: comportamento não humano."),
  ignorarGrupos: z.boolean(),
  ignorarStatus: z.boolean(),
});
export type ConfiguracoesNumeroApi = z.infer<typeof zConfiguracoesNumeroApi>;
export const zAtualizarConfiguracoesNumeroApi = zConfiguracoesNumeroApi
  .partial()
  .refine((d) => Object.keys(d).length > 0, { message: "Informe ao menos uma configuração." });
export type AtualizarConfiguracoesNumeroApi = z.output<typeof zAtualizarConfiguracoesNumeroApi>;

export const DURACOES_SILENCIO = ["8h", "1semana", "sempre"] as const;
export const zSilenciarConversaApi = z.object({ duracao: z.enum(DURACOES_SILENCIO) });
export type SilenciarConversaApi = z.output<typeof zSilenciarConversaApi>;

export const zSincronizarHistoricoApi = z.object({
  telefone: paraCampo.describe("Conversa a sincronizar."),
  quantidade: z.number().int().min(1).max(100).default(50),
});
export type SincronizarHistoricoApi = z.output<typeof zSincronizarHistoricoApi>;

export const zRejeitarChamadaApi = z.object({ numeroId: numeroIdCampo });
export type RejeitarChamadaApi = z.output<typeof zRejeitarChamadaApi>;

export const zConversaMensagensApi = z.object({
  conversa: zConversaApi,
  mensagens: z.object({
    itens: z.array(zMensagemApi).describe("Da mais nova para a mais antiga."),
    proximoCursor: z.string().nullable(),
  }),
});
export type ConversaMensagensApi = z.infer<typeof zConversaMensagensApi>;

// ─── Campanhas ───────────────────────────────────────────────────────────────

const contatoInline = z.object({
  telefone: z.union([z.string(), z.number()]).transform((v) => String(v)).describe("Telefone com DDD (e DDI se não for do Brasil, com `+`)."),
  nome: z.string().trim().max(200).nullish(),
  variaveis: z
    .array(z.union([z.string(), z.number()]).transform((v) => String(v)))
    .max(LISTA_MAX_COLUNAS)
    .optional()
    .describe("Valores das variáveis do template para este contato, na ordem (`{{1}}`, `{{2}}`…). Dispensa o `mapeamento`."),
  campos: z
    .record(z.string().trim().min(1).max(60), z.union([z.string(), z.number(), z.boolean()]).transform((v) => String(v)))
    .refine((c) => Object.keys(c).length <= LISTA_MAX_COLUNAS, {
      message: `Cada contato pode ter no máximo ${LISTA_MAX_COLUNAS} campos extras.`,
    })
    .optional()
    .describe("Campos extras, referenciáveis no `mapeamento`."),
});
export type ContatoInlineApi = z.output<typeof contatoInline>;

const agendamentoApi = z
  .string()
  .trim()
  .refine((v) => parseAgendamentoSaoPaulo(v) !== null, "Data/hora inválida. Use AAAA-MM-DDTHH:mm (horário de Brasília) ou ISO 8601 com fuso.");

/** `POST /v1/campanhas`: número OFICIAL, template e `contatos` inline (até 50.000) OU `listaId`. */
export const zCriarCampanhaApi = z
  .object({
    nome: z.string({ required_error: "Informe o nome da campanha." }).trim().min(1, "Informe o nome da campanha.").max(120),
    numeroId: numeroIdCampo.describe("Número OFICIAL."),
    templateId: idObrigatorio("o template (templateId)").describe("`id` do template em `GET /v1/numeros/{id}/templates`."),
    listaId: idPublico.optional().describe("Lista de contatos já criada no painel. Use `listaId` OU `contatos`."),
    contatos: z
      .array(contatoInline)
      .min(1, "Informe ao menos um contato.")
      .max(LISTA_MAX_LINHAS, `No máximo ${LISTA_MAX_LINHAS.toLocaleString("pt-BR")} contatos por campanha.`)
      .optional()
      .describe("Contatos inline (até 50.000), cada um com suas variáveis."),
    mapeamento: zMapeamento
      .optional()
      .describe("Posição da variável (`\"1\"`) → campo do contato ou valor fixo. Omita quando os contatos trazem `variaveis`."),
    agendadaPara: agendamentoApi.optional().describe("Sem fuso explícito vale o horário de Brasília."),
    pausaAutomatica: z.boolean().default(false),
    iniciar: z.boolean().default(true).describe("true (padrão) dispara agora; ignorado se houver `agendadaPara`. false cria como rascunho."),
  })
  .refine((v) => (v.listaId !== undefined) !== (v.contatos !== undefined), {
    message: "Informe listaId OU contatos (um dos dois).",
    path: ["contatos"],
  });
export type CriarCampanhaApi = z.output<typeof zCriarCampanhaApi>;

export const zConsultaCampanhasApi = z.object({ status: z.enum(STATUS_CAMPANHA).optional() });
export type ConsultaCampanhasApi = z.output<typeof zConsultaCampanhasApi>;

const zTotaisEnvios = z.object({
  pendente: z.number().int(),
  enviado: z.number().int(),
  entregue: z.number().int(),
  lido: z.number().int(),
  respondido: z.number().int(),
  falhou: z.number().int(),
  ignoradoOptout: z.number().int(),
});

export const zEstimativaCustoApi = z.object({
  categoria: z.enum(CATEGORIAS_META),
  quantidade: z.number().int().describe("Contatos que receberão (exclui opt-out)."),
  ignoradosOptout: z.number().int(),
  centavos: z.number().int().describe("Estimativa do que a Meta cobra, em centavos de BRL."),
  porPais: z.array(
    z.object({
      ddi: z.string(),
      rotulo: z.string(),
      quantidade: z.number().int(),
      precoUsd: z.string(),
      centavosPorMensagem: z.number().int(),
      centavos: z.number().int(),
    }),
  ),
  cambio: z.string().nullable(),
  tabelaVersao: z.number().int().nullable(),
  semPreco: z.array(z.string()),
  estimado: z.literal(true),
});
export type EstimativaCustoApi = z.infer<typeof zEstimativaCustoApi>;

export const zCampanhaApi = z.object({
  id: z.string(),
  nome: z.string(),
  status: z.enum(STATUS_CAMPANHA),
  numero: z.object({ id: z.string(), apelido: z.string() }),
  template: z.object({ id: z.string(), nome: z.string() }),
  lista: z.object({ id: z.string(), nome: z.string() }).nullable(),
  agendadaPara: z.string().nullable(),
  iniciadaEm: z.string().nullable(),
  concluidaEm: z.string().nullable(),
  pausaAutomatica: z.boolean(),
  motivoPausa: z.string().nullable(),
  custoEstimadoCentavos: z.number().int(),
  total: z.number().int().describe("Contatos da campanha."),
  totais: zTotaisEnvios,
  alertas: z.array(z.object({ codigo: z.string(), mensagem: z.string() })).describe("Informativos; nunca bloqueiam."),
  taxaFalha: z.number().describe("Falhas ÷ (enviados + falhas), em %."),
  erroMaisFrequente: z.object({ codigo: z.string(), mensagem: z.string(), quantidade: z.number().int() }).nullable(),
  criadoEm: z.string(),
});
export type CampanhaApi = z.infer<typeof zCampanhaApi>;

export const zListaCampanhasApi = z.object({
  campanhas: z.array(
    z.object({
      id: z.string(),
      nome: z.string(),
      status: z.enum(STATUS_CAMPANHA),
      numero: z.object({ id: z.string(), apelido: z.string() }),
      template: z.object({ id: z.string(), nome: z.string() }),
      agendadaPara: z.string().nullable(),
      iniciadaEm: z.string().nullable(),
      concluidaEm: z.string().nullable(),
      custoEstimadoCentavos: z.number().int(),
      total: z.number().int(),
      totais: zTotaisEnvios,
      criadoEm: z.string(),
    }),
  ),
});
export type ListaCampanhasApi = z.infer<typeof zListaCampanhasApi>;

/** 202 de `POST /v1/campanhas`. */
export const zCampanhaCriadaApi = zCampanhaApi.extend({
  estimativa: zEstimativaCustoApi,
  contatos: z
    .object({
      recebidos: z.number().int(),
      aceitos: z.number().int(),
      rejeitados: z.number().int(),
      duplicados: z.number().int(),
      rejeitadasAmostra: z.array(z.object({ linha: z.number().int(), telefone: z.string(), motivo: z.string() })),
    })
    .nullable()
    .describe("Resumo da importação quando a campanha foi criada com `contatos` inline."),
});
export type CampanhaCriadaApi = z.infer<typeof zCampanhaCriadaApi>;

// ─── Leads ───────────────────────────────────────────────────────────────────

const zNumeroRefLead = z.object({ id: z.string(), apelido: z.string(), telefone: z.string().nullable() });

export const zLeadApi = z.object({
  id: z.string(),
  contatoTelefone: z.string(),
  contatoNome: z.string().nullable(),
  origem: z.enum(ORIGENS_LEAD),
  campanha: z.object({ id: z.string(), nome: z.string() }).nullable(),
  anuncio: z.object({ id: z.string(), titulo: z.string().nullable(), url: z.string().nullable() }).nullable(),
  primeiraMensagem: z.string().nullable(),
  status: z.enum(STATUS_LEAD),
  numeroOficial: zNumeroRefLead,
  numeroAtribuido: zNumeroRefLead.nullable(),
  conversaId: z.string().nullable(),
  contatoOptout: z.boolean(),
  repassadoEm: z.string().nullable(),
  respondeuEm: z.string().nullable(),
  criadoEm: z.string(),
  economia: z.object({ qtd: z.number().int(), marketingCentavos: z.number().int(), utilityCentavos: z.number().int() }),
});
export type LeadApi = z.infer<typeof zLeadApi>;

export const zListaLeadsApi = z.object({ itens: z.array(zLeadApi), proximoCursor: z.string().nullable() });
export type ListaLeadsApi = z.infer<typeof zListaLeadsApi>;

export const zResultadoRepasseApi = z.object({ lead: zLeadApi, contatoOptout: z.boolean() });
export type ResultadoRepasseApi = z.infer<typeof zResultadoRepasseApi>;

// ─── Economia ────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` vale o dia inteiro em America/Sao_Paulo (UTC-3); ISO completo vale como está. */
export function parseDataApi(valor: string, fimDoDia: boolean): Date | null {
  const s = valor.trim();
  const somenteData = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const d = new Date(somenteData ? `${s}T${fimDoDia ? "23:59:59.999" : "00:00:00.000"}-03:00` : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dataApi = (fimDoDia: boolean) =>
  z
    .string()
    .trim()
    .min(1)
    .max(40)
    .transform((s, ctx) => {
      const d = parseDataApi(s, fimDoDia);
      if (!d) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Data inválida. Use AAAA-MM-DD ou ISO 8601." });
        return z.NEVER;
      }
      return d;
    });

export const zConsultaEconomiaApi = z.object({
  inicio: dataApi(false).optional().describe("Padrão: primeiro dia do mês corrente (America/Sao_Paulo)."),
  fim: dataApi(true).optional().describe("Padrão: agora."),
  numeroId: idPublico.optional(),
});
export type ConsultaEconomiaApi = z.output<typeof zConsultaEconomiaApi>;

const zValoresEconomia = z.object({ marketingCentavos: z.number().int(), utilityCentavos: z.number().int() });

export const zEconomiaApi = z.object({
  periodo: z.object({ inicio: z.string(), fim: z.string() }).describe("Dias (America/Sao_Paulo), `AAAA-MM-DD`."),
  numeroId: z.string().nullable(),
  totais: zValoresEconomia.extend({ qtd: z.number().int() }).describe("Os mesmos totais do dashboard, em centavos de BRL."),
  porTipoEnvio: z.array(zValoresEconomia.extend({ tipoEnvio: z.enum(TIPOS_ENVIO), qtd: z.number().int() })),
  gastoOficial: z.object({
    centavos: z.number().int(),
    qtd: z.number().int(),
    porCategoria: z.array(z.object({ categoria: z.enum(CATEGORIAS_META), qtd: z.number().int(), centavos: z.number().int() })),
    estimado: z.literal(true),
  }),
  porOrigemLead: z.object({
    campanhas: z.array(zValoresEconomia.extend({ campanhaId: z.string(), nome: z.string(), qtd: z.number().int() })),
    anuncios: z.array(zValoresEconomia.extend({ anuncioId: z.string(), titulo: z.string().nullable(), qtd: z.number().int() })),
  }),
  tabelaPrecos: z
    .object({
      versao: z.number().int(),
      vigenteDe: z.string().describe("Data da tabela de preços da Meta usada no cálculo."),
      desatualizada: z.boolean(),
      cambio: z.object({ usdBrl: z.string(), vigenteDe: z.string(), fonte: z.string() }),
    })
    .nullable(),
});
export type EconomiaApi = z.infer<typeof zEconomiaApi>;

// ─── Destinos de eventos (schemas de entrada em ./eventos) ───────────────────

const zConfigWebhook = z.object({ url: z.string() });
const zConfigRabbit = z.object({
  host: z.string(),
  port: z.number().int(),
  vhost: z.string(),
  usuario: z.string().describe("Mascarado."),
  senha: z.string().describe("Sempre mascarada."),
  exchange: z.string(),
  prefixo: z.string(),
  tls: z.boolean(),
  permitirSemTls: z.boolean(),
  modo: z.enum(["exchange", "filas"]),
});
const zConfigNats = z.object({
  servidores: z.array(z.string()),
  tls: z.boolean(),
  permitirSemTls: z.boolean(),
  autenticacao: z.enum(["nenhuma", "token", "usuario", "creds"]),
  usuario: z.string().nullable().describe("Mascarado."),
  senha: z.string().nullable().describe("Sempre mascarada."),
  prefixo: z.string(),
  jetstream: z.boolean(),
});
const zConfigKafka = z.object({
  brokers: z.array(z.string()),
  tls: z.boolean(),
  permitirSemTls: z.boolean(),
  sasl: z.enum(["nenhum", "plain", "scram-sha-256", "scram-sha-512"]),
  usuario: z.string().nullable().describe("Mascarado."),
  senha: z.string().nullable().describe("Sempre mascarada."),
  topico: z.string().nullable(),
  prefixoTopico: z.string().nullable(),
});
const zConfigSqs = z.object({
  filaUrl: z.string(),
  regiao: z.string(),
  fifo: z.boolean(),
  accessKeyId: z.string().describe("Mascarado."),
  secretAccessKey: z.string().describe("Sempre mascarado."),
});

export const zDestinoEventoApi = z.object({
  id: z.string(),
  tipo: z.enum(["WEBHOOK", "RABBITMQ", "NATS", "KAFKA", "SQS"]),
  nome: z.string(),
  ativo: z.boolean(),
  eventos: z.array(z.enum(EVENTOS_SAIDA)),
  numeroIds: z.array(z.string()).describe("Vazio = todos os números."),
  config: z.union([zConfigWebhook, zConfigRabbit, zConfigNats, zConfigKafka, zConfigSqs]),
  segredoHmac: z.string().describe("Segredo de assinatura MASCARADO. O completo só aparece na criação/rotação."),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
});
export type DestinoEventoApi = z.infer<typeof zDestinoEventoApi>;

export const zDestinoEventoCriadoApi = zDestinoEventoApi.extend({
  segredoHmacRevelado: z.string().optional().describe("Segredo HMAC completo: exibido UMA vez (criação ou rotação). Guarde-o."),
});
export type DestinoEventoCriadoApi = z.infer<typeof zDestinoEventoCriadoApi>;

export const zListaDestinosApi = z.object({ destinos: z.array(zDestinoEventoApi) });
export type ListaDestinosApi = z.infer<typeof zListaDestinosApi>;

export const zResultadoTesteDestinoApi = z.object({
  sucesso: z.boolean(),
  statusHttp: z.number().int().nullable(),
  latenciaMs: z.number().int(),
  erro: z.string().nullable(),
  aviso: z.string().nullable(),
});
export type ResultadoTesteDestinoApi = z.infer<typeof zResultadoTesteDestinoApi>;

export const zEntregaEventoApi = z.object({
  id: z.string(),
  eventoId: z.string(),
  evento: z.string(),
  sequencia: z.number().int(),
  status: z.enum(["PENDENTE", "ENTREGUE", "NAO_ROTEADA", "FALHOU"]),
  tentativas: z.number().int(),
  proximaTentativaEm: z.string().nullable(),
  ultimoErro: z.string().nullable(),
  entregueEm: z.string().nullable(),
  criadoEm: z.string(),
});
export type EntregaEventoApi = z.infer<typeof zEntregaEventoApi>;

export const zListaEntregasApi = z.object({
  itens: z.array(zEntregaEventoApi),
  total: z.number().int(),
  pagina: z.number().int(),
  limite: z.number().int(),
});
export type ListaEntregasApi = z.infer<typeof zListaEntregasApi>;

// ─── Catálogo e WebSocket de eventos ─────────────────────────────────────────

export const zCatalogoEventosApi = z.object({
  eventos: z.array(
    z.object({
      nome: z.enum(EVENTOS_SAIDA),
      descricao: z.string(),
      tiposNumero: z.array(z.enum(["OFICIAL", "NAO_OFICIAL"])).describe("Tipos de número em que o evento ocorre."),
      versao: z.number().int().describe("Versão do formato de `dados`."),
    }),
  ),
  tiposDestino: z.array(z.enum(["WEBHOOK", "RABBITMQ", "NATS", "KAFKA", "SQS"])),
  websocket: z.object({ url: z.string().describe("URL da conexão WebSocket de eventos.") }),
});
export type CatalogoEventosApi = z.infer<typeof zCatalogoEventosApi>;

export const zTicketWsApi = z.object({
  ticket: z.string().describe("Uso único. Conecte em `/v1/eventos/ws?ticket=<ticket>`."),
  expiraEmSegundos: z.number().int(),
});
export type TicketWsApi = z.infer<typeof zTicketWsApi>;
